import { Alert, Link, Spinner } from '@amzn/awsui-components-console';
import type { PermissionClaim } from '../code-generated/PermissionClaim.generated';
import { NOT_LOADED, useDoesUserHavePermissionClaims } from '../context/Permissions';
import * as tokens from '@amzn/awsui-design-tokens-console';
import { styled } from '@linaria/react';

/**
 * RequiresPermissionClaims - Permission-based access control component
 *
 * TESTING GUIDANCE: This component SHOULD be used directly in tests rather than mocked.
 * It provides essential permission validation behavior that should be tested as part of
 * the user experience. The component handles loading states, permission checks, and
 * access denied scenarios that are critical to test for proper application behavior.
 *
 * @example
 * // Use directly in tests - DO NOT MOCK
 * render(
 *   <PermissionsProvider>
 *     <RequiresPermissionClaims claims={[PermissionClaim.SomePermission]}>
 *       <YourComponent />
 *     </RequiresPermissionClaims>
 *   </PermissionsProvider>
 * );
 */
export function RequiresPermissionClaims(props: {
  readonly claims: PermissionClaim[];
  readonly operator?: 'and' | 'or';
  readonly children: React.ReactNode | React.ReactNode[];
}) {
  const hasClaims = useDoesUserHavePermissionClaims(props.claims, props.operator);
  return hasClaims === NOT_LOADED ? (
    <CenteredMessage>
      <Spinner size='large' /> Loading permission claims...
    </CenteredMessage>
  ) : hasClaims ? (
    props.children
  ) : (
    <Alert type='warning'>
      You must have {props.operator === 'or' ? 'at least one' : 'all'} of the necessary permission claims{' '}
      <code>[{props.claims.join(', ')}]</code> for this content to be useful. If you feel you need these permissions,
      please{' '}
      <Link external href='https://t.corp.amazon.com/create/templates/86aab676-aee1-4f86-b39c-f8f5f145188e'>
        cut a ticket
      </Link>
    </Alert>
  );
}

export const CenteredMessage = styled.div`
  background-color: ${tokens.colorBackgroundLayoutMain};
  color: ${tokens.colorTextBodyDefault};
  font-family: ${tokens.fontFamilyBase};
  /* height: 100vh; */
  /* height: 100vh;
  width: 100vw; */
  display: flex;
  justify-content: center;
  align-items: center;
`;
